import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { defineMessages, useIntl, type IntlShape } from "react-intl";

import type { Brand } from "@aglocal/brands";
import { getBrandFromEmail } from "@aglocal/brands";
import type { Interview } from "@aglocal/schema/Interview";
import type { TalentMeetingDetails } from "@aglocal/schema/TalentMeetingDetails";
import {
  PostMeetingForm,
  type PostMeetingSummary,
} from "@aglocal/schema/PostMeetingForm";
import {
  SFDC_LONG_TEXT_LIMIT,
  measureSkillsPayload,
} from "@aglocal/schema/PostMeetingLimits";

import UniqueList from "@aglocal/web/helpers/UniqueList";
import asError from "@aglocal/web/helpers/asError";
import normalizeSkill from "@aglocal/web/helpers/normalizeSkill";
import { effectTsResolver } from "@hookform/resolvers/effect-ts";
import { Schema } from "effect";
import { useMissionControl } from "@aglocal/web/mission-control";
import { InterviewMetricsNotesResponse, ACTIVITY_TYPES } from "@aglocal/types";

import getJobTitle from "@/helpers/getJobTitle";

import { FIELD_LABELS } from "./messages";

export const PostMeetingFormWithSummary = PostMeetingForm;

export type PostMeetingFormWithSummary = typeof PostMeetingFormWithSummary.Type;

export type ReviewedTextField =
  | "goalsAndInterests"
  | "locationComments"
  | "skillsComments"
  | "currentCompany";

export const VALIDATION_MESSAGES = defineMessages({
  jobTitleRequired: {
    defaultMessage: "Please enter a job title",
    description: "Validation error shown when the job title field is empty.",
  },
  summaryOrNotesRequired: {
    defaultMessage: "Please enter a meeting summary or notes",
    description:
      "Validation error shown on the notes and summary fields when both are empty.",
  },
  textLimit: {
    defaultMessage:
      "{field} must not exceed {limit, number} characters (currently {count, number})",
    description:
      "Validation error for a text field over the Salesforce length limit; {field} is the field label, {limit} the maximum and {count} the current length.",
  },
  skillsLimit: {
    defaultMessage:
      "The Skills list exceeds the {limit, number} character Salesforce limit by {excess, number} characters. Remove some skills.",
    description:
      "Validation error when the skills list is over the Salesforce length limit; {limit} is the maximum and {excess} how many characters over it is.",
  },
});

/** Long text fields whose length Salesforce limits, keyed by form field name. */
const LIMITED_TEXT_FIELDS = [
  "skillsComments",
  "goalsAndInterests",
  "locationComments",
] as const;

/**
 * Builds the wrap-up form schema with validation messages in the user's
 * language. The job title rule from the shared schema is re-declared here
 * because its message is fixed in English.
 */
export function createPostMeetingFormSchema(intl: IntlShape) {
  const { formatMessage } = intl;

  return Schema.extend(
    PostMeetingForm.omit("jobTitle"),
    Schema.Struct({
      jobTitle: Schema.String.pipe(
        Schema.nonEmptyString({
          message: () => formatMessage(VALIDATION_MESSAGES.jobTitleRequired),
        }),
      ),
    }),
  ).pipe(
    Schema.filter((data) => {
      const issues: Schema.FilterIssue[] = [];

      if (data.notes.trim() === "" && data.summary.value.trim() === "") {
        const message = formatMessage(
          VALIDATION_MESSAGES.summaryOrNotesRequired,
        );
        issues.push(
          { path: ["notes"], message },
          { path: ["summary", "value"], message },
        );
      }

      for (const field of LIMITED_TEXT_FIELDS) {
        const count = data[field].length;
        if (count > SFDC_LONG_TEXT_LIMIT) {
          issues.push({
            path: [field],
            message: formatMessage(VALIDATION_MESSAGES.textLimit, {
              field: formatMessage(FIELD_LABELS[field]),
              limit: SFDC_LONG_TEXT_LIMIT,
              count,
            }),
          });
        }
      }

      // Salesforce stores skills as JSON, so the limit applies to the serialized payload
      const skillsPayload = measureSkillsPayload(
        Array.from(toUniqueSkillList(data.skills)),
      );
      if (skillsPayload > SFDC_LONG_TEXT_LIMIT) {
        issues.push({
          path: ["skills"],
          message: formatMessage(VALIDATION_MESSAGES.skillsLimit, {
            limit: SFDC_LONG_TEXT_LIMIT,
            excess: skillsPayload - SFDC_LONG_TEXT_LIMIT,
          }),
        });
      }

      return issues;
    }),
  );
}

function getDefaultMeetingType(
  candidateStatus: string | undefined,
  brand: Brand | null,
): (typeof ACTIVITY_TYPES)[number] {
  if (brand?.defaultActivityType) {
    return brand.defaultActivityType;
  }
  if (candidateStatus === "Current") {
    return "STP";
  }
  return "G2";
}

function prependExtractedText(
  existing: string | null | undefined,
  extracted?: string,
) {
  const previous = existing ?? "";
  if (!extracted?.trim()) {
    return previous;
  }
  // The profile may already contain this extraction after saving and reopening.
  if (previous === extracted || previous.startsWith(`${extracted}\n\n`)) {
    return previous;
  }
  return previous.trim() ? `${extracted}\n\n${previous}` : extracted;
}

function useDefaultValues(
  interview: Interview,
  interviewMetrics?: InterviewMetricsNotesResponse,
  extractedDetails?: TalentMeetingDetails,
): PostMeetingFormWithSummary {
  const { id, interviewee, interviewer, inMeetingNotes = "" } = interview;
  const { connectedDetails } = interviewee ?? {};
  const { candidateStatus } = connectedDetails ?? {};
  const currentJobTitle = getJobTitle(interview);
  // The API's current profile text, including explicit empty strings, is
  // authoritative. The page stays hidden until this read succeeds.
  const profileNotes = interviewMetrics?.profileNotes;
  const profileGoals = profileNotes?.goalsAndInterests ?? "";
  const profileLocation = profileNotes?.locationComments ?? "";
  const skillsComments = profileNotes?.skillsComments ?? "";
  const profileCompany = profileNotes?.currentCompany ?? "";

  const brand = getBrandFromEmail(interviewer?.uId ?? "");
  const extractedGoals = extractedDetails?.goalsAndInterests;
  const preferences = extractedDetails?.locationPreferences;
  const extractedLocation = preferences?.comments?.trim()
    ? preferences.comments
    : preferences?.preferredLocations
        ?.map(({ city, state, country }) =>
          [city, state, country].filter((part) => part?.trim()).join(", "),
        )
        .filter(Boolean)
        .join("; ");
  // Saved activities can be reopened for editing. Their reviewed profile text
  // is authoritative, including text the recruiter deliberately changed/cleared.
  const hasSavedActivity =
    interviewMetrics?.activityType != null ||
    interviewMetrics?.activityTypeObject != null ||
    interviewMetrics?.editable === false;
  const currentCompany =
    !hasSavedActivity && extractedDetails?.currentCompany
      ? extractedDetails.currentCompany
      : profileCompany;

  return useMemo(() => {
    // Type the activity type more specifically and add validation
    const existingActivityType = interviewMetrics?.activityType as
      | (typeof ACTIVITY_TYPES)[number]
      | undefined;

    // Validate that the activity type is one of the allowed values
    const isValidActivityType = (
      type: string,
    ): type is (typeof ACTIVITY_TYPES)[number] =>
      ACTIVITY_TYPES.includes(type as (typeof ACTIVITY_TYPES)[number]);

    // Determine meeting type with proper type safety
    const defaultMeetingType = getDefaultMeetingType(candidateStatus, brand);
    const meetingType =
      existingActivityType && isValidActivityType(existingActivityType)
        ? existingActivityType
        : defaultMeetingType;

    return {
      id,
      meetingType: meetingType,
      jobTitle: currentJobTitle ?? "",
      currentCompany: currentCompany,
      notes: interviewMetrics?.recruiterNotes ?? inMeetingNotes,
      summary: { value: interviewMetrics?.summary ?? "" },
      skills: [],
      skillsComments,
      goalsAndInterests: prependExtractedText(
        profileGoals,
        hasSavedActivity ? undefined : extractedGoals,
      ),
      negativeSkills: [],
      locationComments: prependExtractedText(
        profileLocation,
        hasSavedActivity ? undefined : extractedLocation,
      ),
      additionalDetails: {}, // Use the default object here
    };
  }, [
    id,
    candidateStatus,
    brand,
    inMeetingNotes,
    currentJobTitle,
    currentCompany,
    skillsComments,
    profileGoals,
    profileLocation,
    extractedGoals,
    extractedLocation,
    hasSavedActivity,
    interviewMetrics?.activityType,
    interviewMetrics?.recruiterNotes,
    interviewMetrics?.summary,
  ]);
}

export function usePostMeetingFormWithSummary(
  interview: Interview,
  extractedDetails?: TalentMeetingDetails,
) {
  const intl = useIntl();
  const api = useMissionControl();
  const [metrics, setMetrics] = useState<{
    id: string;
    value?: InterviewMetricsNotesResponse;
    loading: boolean;
    error?: Error;
  }>({ id: interview.id, loading: true });
  const interviewMetrics =
    metrics.id === interview.id ? metrics.value : undefined;
  const isLoadingMetrics = metrics.id !== interview.id || metrics.loading;
  const metricsError = metrics.id === interview.id ? metrics.error : undefined;

  useEffect(() => {
    let cancelled = false;
    const fetchMetrics = async () => {
      setMetrics((current) => ({
        id: interview.id,
        value: current.id === interview.id ? current.value : undefined,
        loading: true,
      }));
      try {
        const response = await api.getConnectedNoteDetails(interview.id);
        if (
          typeof response.profileNotes?.goalsAndInterests !== "string" ||
          typeof response.profileNotes.locationComments !== "string" ||
          typeof response.profileNotes.skillsComments !== "string" ||
          typeof response.profileNotes.currentCompany !== "string"
        ) {
          throw new Error("Unable to load the latest Connected profile notes");
        }
        if (!cancelled) {
          setMetrics({ id: interview.id, value: response, loading: false });
        }
      } catch (error) {
        if (!cancelled) {
          const loadError = asError(error);
          reportError(loadError);
          setMetrics({ id: interview.id, loading: false, error: loadError });
        }
      }
    };

    void fetchMetrics();
    return () => {
      cancelled = true;
    };
  }, [api, interview.id]);

  const defaultValues = useDefaultValues(
    interview,
    interviewMetrics,
    // A failed metrics read cannot prove this is an unsaved wrap-up. Preserve
    // profile text in that case; the page stays hidden while metrics load.
    isLoadingMetrics || interviewMetrics != null ? extractedDetails : undefined,
  );

  // Rebuilt when the language changes so validation messages follow it
  const resolver = useMemo(
    () => effectTsResolver(createPostMeetingFormSchema(intl)),
    [intl],
  );

  const form = useForm({
    defaultValues,
    resolver,
  });

  const idRef = useRef(interview.id);
  const editedTextFields = useRef({
    id: interview.id,
    fields: new Set<ReviewedTextField>(),
  });
  const isTextFieldEdited = useCallback(
    (name: ReviewedTextField) =>
      editedTextFields.current.id === interview.id &&
      editedTextFields.current.fields.has(name),
    [interview.id],
  );
  const markTextFieldEdited = useCallback(
    (name: ReviewedTextField) => {
      if (editedTextFields.current.id !== interview.id) {
        editedTextFields.current = { id: interview.id, fields: new Set() };
      }
      editedTextFields.current.fields.add(name);
    },
    [interview.id],
  );
  const { reset, watch } = form;
  // keepDirtyValues requires a subscription to dirtyFields.
  const { dirtyFields } = form.formState;

  useEffect(() => {
    const subscription = watch((_values, { name, type }) => {
      if (
        type === "change" &&
        (name === "goalsAndInterests" ||
          name === "locationComments" ||
          name === "skillsComments" ||
          name === "currentCompany")
      ) {
        markTextFieldEdited(name);
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [watch, markTextFieldEdited]);

  useEffect(() => {
    const sameInterview = idRef.current === interview.id;
    if (!sameInterview) {
      editedTextFields.current = { id: interview.id, fields: new Set() };
    }
    // Late defaults must preserve reviewed text (including cleared values),
    // recruiter edits, and skills/details already synced by their fields.
    reset(
      (currentValues) => ({
        ...defaultValues,
        ...(sameInterview && {
          skills: currentValues.skills,
          // Retain the extracted baseline for reviewed details so exclusions
          // stay dirty and later option refreshes cannot silently reselect them.
          additionalDetails: form.getFieldState("additionalDetails").isDirty
            ? ((form.formState.defaultValues?.additionalDetails ??
                {}) as PostMeetingFormWithSummary["additionalDetails"])
            : currentValues.additionalDetails,
          ...(isTextFieldEdited("goalsAndInterests") && {
            goalsAndInterests: currentValues.goalsAndInterests,
          }),
          ...(isTextFieldEdited("locationComments") && {
            locationComments: currentValues.locationComments,
          }),
          ...(isTextFieldEdited("skillsComments") && {
            skillsComments: currentValues.skillsComments,
          }),
          ...(isTextFieldEdited("currentCompany") && {
            currentCompany: currentValues.currentCompany,
          }),
        }),
      }),
      { keepDirtyValues: sameInterview, keepErrors: sameInterview },
    );
    idRef.current = interview.id;
  }, [defaultValues, reset, interview.id, isTextFieldEdited, form]);

  return {
    ...form,
    dirtyFields,
    isTextFieldEdited,
    markTextFieldEdited,
    profileNotes: interviewMetrics?.profileNotes,
    isMeetingTypeDisabled: interviewMetrics?.activityType != null,
    isLoadingMetrics,
    metricsError,
    disableSave:
      isLoadingMetrics ||
      metricsError != null ||
      interviewMetrics?.editable === false,
  };
}

function toUniqueSkillList(skills?: readonly string[]): UniqueList<string> {
  const list = new UniqueList<string>([], normalizeSkill);
  if (!skills) {
    return list;
  }

  skills.forEach((skill) => {
    if (typeof skill !== "string") {
      return;
    }
    const cleaned = skill.trim().replace(/\s+/g, " ");
    if (cleaned.length > 0) {
      list.add(cleaned);
    }
  });

  return list;
}

/**
 * Prepares a generated summary for the form. When generation produced no text
 * (for example with an empty transcript) the model settings are dropped, so a
 * summary the recruiter then writes by hand is not attributed to that model.
 * The name is kept so edits stay cached per model.
 */
export function toFormSummary(summary: PostMeetingSummary): PostMeetingSummary {
  if (summary.value.trim() !== "") {
    return summary;
  }
  return { name: summary.name, value: summary.value };
}

export function getConnectedDetailsUpdate(
  { notes, summary, ...data }: PostMeetingFormWithSummary,
  existingSkills: readonly string[] = [],
): PostMeetingForm {
  const updatedSkills = toUniqueSkillList(data.skills);
  const priorSkills = toUniqueSkillList(existingSkills);
  const removedSkills = UniqueList.difference(priorSkills, updatedSkills);

  return {
    ...data,
    skills: Array.from(updatedSkills),
    negativeSkills: Array.from(removedSkills),
    notes: notes,
    // name and settings travel with the summary so the server can record which model produced it
    summary,
  };
}
